import type { Currency } from "@loot-os/api-contract";

import { Skeleton } from "../../core/Skeleton.js";
import { BoxCard } from "../../domain/BoxCard.js";
import { tv } from "../../foundations/cn.js";
import type { BoxCardContent } from "./content.js";

const gridStyles = tv({
  base: "grid gap-3 md:gap-5",
  variants: {
    columns: {
      /** A browse row: two on phones, five on a wide screen. */
      row: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
      /** A catalog grid beside nothing else. */
      grid: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5",
      /** A few boxes worth a close look: one a row on phones, three on a wide screen. */
      large: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    },
  },
  defaultVariants: { columns: "row" },
});

type Columns = NonNullable<Parameters<typeof gridStyles>[0]>["columns"];

export interface BoxCardsProps {
  cards: readonly BoxCardContent[];
  currency: Currency;
  columns?: Columns;
  className?: string;
}

/**
 * One box's card from its content, the one way a template draws a `BoxCardContent`, so a grid and
 * a rail show the same parts.
 */
export function ContentBoxCard({
  card: { box, href, favorite, topPull, topCard, tag, volatility, volatilityWords },
  currency,
}: {
  card: BoxCardContent;
  currency: Currency;
}) {
  return (
    <BoxCard
      name={box.name}
      image={box.imageUrl}
      price={box.priceCents}
      href={href}
      favorite={favorite}
      topPull={topPull}
      topCard={topCard}
      tag={tag}
      volatility={volatility}
      volatilityWords={volatilityWords}
      currency={currency}
    />
  );
}

/** Boxes as linked cards, each with the shopper's heart. */
export function BoxCards({ cards, currency, columns, className }: BoxCardsProps) {
  return (
    <ul className={gridStyles({ columns, className })}>
      {cards.map((card) => (
        <li key={card.box.id}>
          <ContentBoxCard card={card} currency={currency} />
        </li>
      ))}
    </ul>
  );
}

/** The cards' place while they load, shaped like them so nothing shifts when they land. */
export function BoxCardsSkeleton({
  count = 5,
  columns,
  className,
}: {
  count?: number;
  columns?: Columns;
  className?: string;
}) {
  return (
    <ul aria-hidden className={gridStyles({ columns, className })}>
      {Array.from({ length: count }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity of their own.
        <li key={i}>
          <BoxCardSkeleton />
        </li>
      ))}
    </ul>
  );
}

/** One box card's outline while it loads: the art's well, the name and a short line. */
export function BoxCardSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-3 rounded-2xl bg-bg-surface p-3 pb-4">
      <Skeleton className="aspect-[216/260] w-full rounded-lg" />
      <Skeleton className="h-4 w-3/4 rounded-xs" />
      <Skeleton className="h-4 w-1/3 rounded-xs" />
    </div>
  );
}
